// Shared readers for the marker JSON the API returns. Values may arrive as
// plain values or wrapped as { present, value } (JsonNullable), so every
// reader unwraps first.

export const unwrap = (value) => {
  if (value && typeof value === 'object' && 'present' in value) return value.present ? (value.value ?? null) : null;
  return value ?? null;
};

export const text = (value) => {
  const v = unwrap(value);
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};

export const list = (value) => {
  const v = unwrap(value);
  if (Array.isArray(v)) return v.map(text).filter(Boolean);
  return text(v) ? [text(v)] : [];
};

export const number = (value) => {
  const v = unwrap(value);
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const clock = (value) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(text(value));
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '';
};

export const markerId = (marker) => marker?.id ?? marker?._id ?? null;

export const isEventMarker = (marker) => text(marker?.markerType).toLowerCase() === 'event';

// ---- Days -------------------------------------------------------------------

export const WEEKDAYS = [
  { value: 'Monday', label: 'Montag', short: 'Mo', index: 1 },
  { value: 'Tuesday', label: 'Dienstag', short: 'Di', index: 2 },
  { value: 'Wednesday', label: 'Mittwoch', short: 'Mi', index: 3 },
  { value: 'Thursday', label: 'Donnerstag', short: 'Do', index: 4 },
  { value: 'Friday', label: 'Freitag', short: 'Fr', index: 5 },
  { value: 'Saturday', label: 'Samstag', short: 'Sa', index: 6 },
  { value: 'Sunday', label: 'Sonntag', short: 'So', index: 0 },
];

const DAY_ALIASES = {
  monday: 'Monday', montag: 'Monday', mo: 'Monday', mon: 'Monday', 1: 'Monday',
  tuesday: 'Tuesday', dienstag: 'Tuesday', di: 'Tuesday', tue: 'Tuesday', 2: 'Tuesday',
  wednesday: 'Wednesday', mittwoch: 'Wednesday', mi: 'Wednesday', wed: 'Wednesday', 3: 'Wednesday',
  thursday: 'Thursday', donnerstag: 'Thursday', do: 'Thursday', thu: 'Thursday', 4: 'Thursday',
  friday: 'Friday', freitag: 'Friday', fr: 'Friday', fri: 'Friday', 5: 'Friday',
  saturday: 'Saturday', samstag: 'Saturday', sa: 'Saturday', sat: 'Saturday', 6: 'Saturday',
  sunday: 'Sunday', sonntag: 'Sunday', so: 'Sunday', sun: 'Sunday', 0: 'Sunday',
};

export const normalizeDay = (day) => DAY_ALIASES[text(day).toLowerCase().replace(/_/g, '')] ?? null;

export const weekdayMeta = (day) => WEEKDAYS.find((d) => d.value === normalizeDay(day)) ?? null;

/** Opening hours as { Monday: {open, close}, ... } for days with both times. */
export const openingHours = (marker) => {
  const hours = {};
  const raw = unwrap(marker?.weekdays);
  if (!Array.isArray(raw)) return hours;
  raw.forEach((entry) => {
    const day = normalizeDay(entry?.day);
    const open = clock(entry?.open);
    const close = clock(entry?.close);
    if (day && open && close) hours[day] = { open, close };
  });
  return hours;
};

export const openDayShorts = (marker) => {
  const hours = openingHours(marker);
  return WEEKDAYS.filter((d) => hours[d.value]).map((d) => d.short);
};

const minutes = (hhmm) => {
  const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
};

/** The opening slot that covers `now`: today's, or yesterday's if it ran past midnight. */
export const currentOpening = (marker, now = new Date()) => {
  const hours = openingHours(marker);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const today = WEEKDAYS.find((d) => d.index === now.getDay());
  const yesterday = WEEKDAYS.find((d) => d.index === (now.getDay() + 6) % 7);

  const slot = hours[today.value];
  if (slot) {
    const open = minutes(slot.open);
    const close = minutes(slot.close);
    if (close > open ? nowMin >= open && nowMin < close : nowMin >= open) return { ...slot, day: today.value };
  }
  const before = hours[yesterday.value];
  if (before) {
    const open = minutes(before.open);
    const close = minutes(before.close);
    if (close <= open && nowMin < close) return { ...before, day: yesterday.value };
  }
  return null;
};

export const isOpenNow = (marker, now = new Date()) => !isEventMarker(marker) && currentOpening(marker, now) != null;

export const todaysHours = (marker, now = new Date()) => {
  const today = WEEKDAYS.find((d) => d.index === now.getDay());
  return openingHours(marker)[today.value] ?? null;
};

// ---- Dates ------------------------------------------------------------------

export const isoDate = (marker) => {
  const v = text(marker?.eventDate);
  return /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : '';
};

const atTime = (iso, hhmm) => {
  if (!iso) return null;
  const d = new Date(`${iso}T${hhmm || '00:00'}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const eventStart = (marker) => atTime(isoDate(marker), clock(marker?.startTime));

export const eventEnd = (marker) => {
  const start = eventStart(marker);
  const end = atTime(isoDate(marker), clock(marker?.endTime));
  if (!start || !end) return start;
  if (end <= start) end.setDate(end.getDate() + 1);
  return end;
};

export const isPastEvent = (marker, now = new Date()) => {
  if (!isEventMarker(marker)) return false;
  const end = eventEnd(marker);
  return end ? end < now : false;
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** "Heute", "Morgen", "Gestern" or "Fr, 19.09.2026". */
export const dayLabel = (iso, now = new Date()) => {
  const date = atTime(iso, '00:00');
  if (!date) return '';
  const diff = Math.round((startOfDay(date) - startOfDay(now)) / 86_400_000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  if (diff === -1) return 'Gestern';
  return date.toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const createdAt = (marker) => {
  const v = text(marker?.createdAt);
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const priceLabel = (value) => {
  const n = number(value);
  if (n == null) return '';
  return n === 0 ? 'Gratis' : `€ ${n}`;
};

/** Everything the search box matches against, lower-cased. */
export const searchHaystack = (marker) => [
  text(marker?.title),
  text(marker?.address),
  text(marker?.instagramName),
  text(marker?.specialType),
  ...list(marker?.musicGenre),
  ...(Array.isArray(unwrap(marker?.artists)) ? unwrap(marker.artists).map((a) => text(a?.name)) : []),
  String(markerId(marker) ?? ''),
].join(' ').toLowerCase();
